import { APIError, APIErrorType, type BenchmarkView, type SearchBenchmarksQueryVariables } from '@vizit/api';
import { useSearchBenchmarksQuery } from '@vizit/api';
import type { BenchmarkItem } from '@vizit/organisms';
import * as React from 'react';

enum BenchmarkSortOption {
  Newest = 'newest',
  Oldest = 'oldest',
  Alphabetical = 'alphabetical',
}

const SORT_QUERY: Record<BenchmarkSortOption, Pick<SearchBenchmarksQueryVariables, 'sort_by' | 'sort_direction'>> = {
  [BenchmarkSortOption.Newest]: { sort_by: 'created_at', sort_direction: 'desc' },
  [BenchmarkSortOption.Oldest]: { sort_by: 'created_at', sort_direction: 'asc' },
  [BenchmarkSortOption.Alphabetical]: { sort_by: 'name', sort_direction: 'asc' },
};

function toBenchmarkItem(benchmark: BenchmarkView): BenchmarkItem {
  return {
    id: benchmark.id,
    name: benchmark.name,
    description: benchmark.description,
    categoryName: benchmark.product_category_name,
    imageCount: benchmark.image_count,
    previewImageUrls: benchmark.preview_image_urls,
    creatorName: benchmark.created_by.name,
  };
}

/** URL search params that hold the Benchmarks view filters, next to the `collection*` ones. */
enum BenchmarkGridFilterOptions {
  Search = 'benchmarkSearchTerm',
  ProductCategory = 'benchmarkCategoryId',
  ProductCategoryName = 'benchmarkCategoryName',
  Sort = 'benchmarkSort',
}

type BenchmarkGridFilters = Partial<Record<BenchmarkGridFilterOptions, string>>;

function parseBenchmarkSortOption(value: unknown): BenchmarkSortOption {
  return value === BenchmarkSortOption.Oldest || value === BenchmarkSortOption.Alphabetical
    ? value
    : BenchmarkSortOption.Newest;
}

interface UseBenchmarkGridProps {
  workspaceId?: string;
  /** Current filters, read from the URL by the route */
  filters?: BenchmarkGridFilters;
  /** Called to change one filter; `undefined` clears it */
  onFilterChange?: (filter: BenchmarkGridFilterOptions, value?: string) => void;
  onAuthenticationError?: (error: APIError) => void;
}

/**
 * Data for the Library's Benchmarks view, driven by filters the route keeps
 * in the URL so they survive switching views, changing tab and Back. The
 * search box debounces before calling `onSearchTermChange`. A failed request
 * surfaces as `isError` so the view can tell it apart from an empty result;
 * a sign-in failure goes to `onAuthenticationError`.
 */
function useBenchmarkGrid({ workspaceId, filters = {}, onFilterChange, onAuthenticationError }: UseBenchmarkGridProps) {
  const searchTerm = filters[BenchmarkGridFilterOptions.Search] ?? '';
  const productCategoryId = filters[BenchmarkGridFilterOptions.ProductCategory];
  const productCategoryName = filters[BenchmarkGridFilterOptions.ProductCategoryName] ?? '';
  const sortOption = parseBenchmarkSortOption(filters[BenchmarkGridFilterOptions.Sort]);

  const { data, isLoading, isError, error, refetch } = useSearchBenchmarksQuery(
    {
      workspace_id: workspaceId ?? '',
      name_like: searchTerm || undefined,
      product_category_id: productCategoryId,
      ...SORT_QUERY[sortOption],
    },
    { enabled: !!workspaceId },
  );

  React.useEffect(() => {
    if (error instanceof APIError && error.type === APIErrorType.NotAuthenticated) {
      onAuthenticationError?.(error);
    }
  }, [error, onAuthenticationError]);

  const benchmarks = React.useMemo(() => (data ?? []).map(toBenchmarkItem), [data]);

  const onSearchTermChange = React.useCallback(
    (value: string) => onFilterChange?.(BenchmarkGridFilterOptions.Search, value.trim() || undefined),
    [onFilterChange],
  );

  const onFilterProductCategory = React.useCallback(
    (id: string | null, name: string) => {
      onFilterChange?.(BenchmarkGridFilterOptions.ProductCategory, id || undefined);
      onFilterChange?.(BenchmarkGridFilterOptions.ProductCategoryName, (id && name) || undefined);
    },
    [onFilterChange],
  );

  const onSortOptionChange = React.useCallback(
    (option: BenchmarkSortOption) =>
      // Newest is the default, so it stays out of the URL.
      onFilterChange?.(BenchmarkGridFilterOptions.Sort, option === BenchmarkSortOption.Newest ? undefined : option),
    [onFilterChange],
  );

  return {
    benchmarks,
    // A disabled query reports isLoading false, so a workspace that has not resolved yet counts as loading.
    isLoading: isLoading || !workspaceId,
    isError,
    onRetry: refetch,
    searchTerm,
    productCategoryId,
    productCategoryName,
    sortOption,
    onSearchTermChange,
    onFilterProductCategory,
    onSortOptionChange,
  };
}

export { BenchmarkGridFilterOptions, BenchmarkSortOption, parseBenchmarkSortOption, useBenchmarkGrid };
export type { BenchmarkGridFilters };
